import CatalogLink from '@/components/Catalog/CatalogLink';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { StyleSheet, View, Platform } from 'react-native';
import { Image } from 'expo-image';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import PromptFavoriteHeart from '@/components/Prompts/PromptFavoriteHeart';
import useResponsiveness from '@/hooks/useResponsiveness';
import { CATALOG_COVER_ASPECT } from '@/constants/catalogMedia';

// Match the generated thumbnail ratio (1536×1024 = 3:2).
const ASPECT = CATALOG_COVER_ASPECT;
const isWeb = Platform.OS === 'web';
const LAZY_ROOT_MARGIN = '400px 0px';

/** Defer media fetch until the card is near the viewport (web); native loads immediately. */
function useLazyInView() {
  const ref = useRef(null);
  const [inView, setInView] = useState(!isWeb);

  useEffect(() => {
    if (!isWeb) return;

    const target = ref.current;
    if (!target || typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: LAZY_ROOT_MARGIN, threshold: 0 },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, []);

  return { ref, inView };
}

// Only attach hover handlers on devices with a real pointer — avoids double-tap
// on touch-only mobile web (same pattern as ExploreCard).
const SUPPORTS_HOVER = (() => {
  if (!isWeb || typeof window === 'undefined' || !window.matchMedia) return false;
  try {
    return window.matchMedia('(hover: hover)').matches;
  } catch {
    return false;
  }
})();

function WebHoverVideo({ uri, playing }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (playing) {
      el.currentTime = 0;
      el.play().catch(() => {});
    } else {
      el.pause();
    }
  }, [playing, uri]);

  if (!playing) return null;

  return (
    <video
      ref={ref}
      src={uri}
      loop
      muted
      playsInline
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
      }}
    />
  );
}

function PromptCard({ prompt, onPress, measuredCardWidth }) {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const [hovered, setHovered] = useState(false);
  const { ref: mediaRef, inView: isMediaInView } = useLazyInView();

  const thumb = prompt?.exampleThumbnailUrl || prompt?.exampleMediaUrl || '';
  const blurhash = prompt?.exampleBlurhash || undefined;
  const shouldLoadMedia = isMediaInView && !!thumb;
  const isVideo = prompt?.outputType === 'video';
  const videoUrl = prompt?.exampleVideoUrl || '';
  const showHoverVideo = isVideo && !!videoUrl && SUPPORTS_HOVER && hovered;

  const styles = useMemo(() => StyleSheet.create({
    card: {
      width: measuredCardWidth != null && measuredCardWidth > 0 ? measuredCardWidth : '100%',
      borderRadius: 12,
      overflow: 'hidden',
      borderWidth: 1,
      borderColor: hovered ? colors.border_color_primary : colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
      ...(Platform.OS === 'web' && { cursor: 'pointer', transition: 'border-color 0.15s ease' }),
    },
    mediaWrap: {
      width: '100%',
      aspectRatio: ASPECT,
      backgroundColor: colors.solid_color_tertiary,
      position: 'relative',
    },
    media: {
      ...StyleSheet.absoluteFillObject,
    },
    playOverlay: {
      ...StyleSheet.absoluteFillObject,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(0,0,0,0.12)',
    },
    playCircle: {
      width: 44,
      height: 44,
      borderRadius: 22,
      backgroundColor: 'rgba(0,0,0,0.55)',
      alignItems: 'center',
      justifyContent: 'center',
    },
    badgeRow: {
      position: 'absolute',
      top: 8,
      left: 8,
      flexDirection: 'row',
      gap: 6,
    },
    favoriteRow: {
      position: 'absolute',
      top: 6,
      right: 6,
      zIndex: 2,
    },
    badge: {
      paddingHorizontal: 8,
      paddingVertical: 3,
      borderRadius: 999,
      backgroundColor: 'rgba(0,0,0,0.55)',
    },
    badgeText: {
      color: '#fff',
      fontSize: 11,
      fontWeight: '600',
      textTransform: 'capitalize',
    },
    titleWrap: {
      paddingHorizontal: isXS ? 10 : 12,
      paddingVertical: isXS ? 10 : 12,
    },
    title: {
      lineHeight: isXS ? 18 : 20,
    },
  }), [colors, hovered, isXS, measuredCardWidth]);

  return (
    <CatalogLink
      href={prompt?.slug ? `/prompts/${encodeURIComponent(prompt.slug)}` : undefined}
      onPress={() => onPress?.(prompt)}
      onHoverIn={SUPPORTS_HOVER ? () => setHovered(true) : undefined}
      onHoverOut={SUPPORTS_HOVER ? () => setHovered(false) : undefined}
      style={styles.card}
      accessibilityRole="button"
      accessibilityLabel={prompt?.name}
    >
      <View ref={mediaRef} style={styles.mediaWrap}>
        {shouldLoadMedia ? (
          <Image
            accessibilityLabel={prompt?.name || ''}
            source={{ uri: thumb }}
            style={styles.media}
            contentFit="cover"
            placeholder={blurhash ? { blurhash } : undefined}
            transition={isWeb ? 0 : 200}
            recyclingKey={prompt?.id || prompt?.slug}
            priority="low"
          />
        ) : !thumb ? (
          <View style={[styles.media, { alignItems: 'center', justifyContent: 'center' }]}>
            <Icon name={isVideo ? 'video' : 'image'} size={28} color={colors.text_color_tertiary} />
          </View>
        ) : null}
        {showHoverVideo && isMediaInView ? <WebHoverVideo uri={videoUrl} playing /> : null}
        <View style={styles.badgeRow}>
          {prompt?.outputType ? (
            <View style={styles.badge}>
              <Text style={styles.badgeText}>{prompt.outputType}</Text>
            </View>
          ) : null}
          {prompt?.inputType === 'image' ? (
            <View style={styles.badge}>
              <Icon name="upload" size={10} color="#fff" solid />
            </View>
          ) : null}
        </View>
        <View style={styles.favoriteRow}>
          <PromptFavoriteHeart prompt={prompt} />
        </View>
        {isVideo && !showHoverVideo ? (
          <View style={styles.playOverlay} pointerEvents="none">
            <View style={styles.playCircle}>
              <Icon name="play" size={16} color="#fff" solid />
            </View>
          </View>
        ) : null}
      </View>
      <View style={styles.titleWrap}>
        <Text weight="semibold" size="s" numberOfLines={2} style={styles.title}>
          {prompt?.name || prompt?.slug}
        </Text>
      </View>
    </CatalogLink>
  );
}

export function PromptCardPlaceholder({ measuredCardWidth }) {
  const { colors } = useTheme();
  const styles = useMemo(() => StyleSheet.create({
    card: {
      width: measuredCardWidth != null && measuredCardWidth > 0 ? measuredCardWidth : '100%',
      borderRadius: 12,
      overflow: 'hidden',
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
    },
    media: {
      width: '100%',
      aspectRatio: ASPECT,
      backgroundColor: colors.solid_color_tertiary,
    },
    title: {
      height: 14,
      margin: 12,
      borderRadius: 6,
      backgroundColor: colors.solid_color_tertiary,
      width: '70%',
    },
  }), [colors, measuredCardWidth]);

  return (
    <View style={styles.card}>
      <View style={styles.media} />
      <View style={styles.title} />
    </View>
  );
}

export default React.memo(PromptCard);
